import { CSSProperties, useState } from "react";
import { Variable } from "../../../models/interactions/Variable";
import RichTextEditor from "../../rich_text/RichTextEditor";
import RichTextView from "../../rich_text/RichTextView";
import { stripFormattingTags } from "../../rich_text/RichTextHtmlUtils";
import "./PowerSlideCanvas.css";
import {
  powerSlideRadius,
  powerSlideType,
  PowerSlideTypeStyle,
} from "./PowerSlideDesign";
import { PowerSlideSurfaceStyle } from "./PowerSlideSurfaces";

interface PowerSlideTextBlockProps {
  localizationKey: string;
  placeholder: string;
  /**
   * Draws the placeholder smaller than the text it stands in for. A worded
   * prompt at display type has nowhere to go in a narrow column and breaks
   * mid-word; the hint is chrome, so it can be sized like chrome. The line box
   * still comes from the field's own type, so an empty region occupies exactly
   * the height a filled one will.
   */
  placeholderFontSize?: number;
  ariaLabel: string;
  typeStyle: PowerSlideTypeStyle;
  color: string;
  surface: PowerSlideSurfaceStyle;
  paddingInline: number;
  paddingBlock: number;
  radius?: number;
  align?: CSSProperties["textAlign"];
  /** Hug the text instead of filling the row, as the Section eyebrow does. */
  hug?: boolean;
  /**
   * Render as plain text with no affordance, for the rail's miniatures. Empty
   * regions disappear, exactly as they do at runtime, so a thumbnail shows the
   * slide rather than a form.
   */
  readOnly?: boolean;
  /** Sequence variables, for token chips and intellisense. */
  variables: Variable[];
  /** The language edits are written to; also flushes pending edits on change. */
  languageShortcode: string;
  getValue: (key: string) => string;
  changeValue: (key: string, value: string, shortcode?: string) => void;
}

/**
 * One editable text region. The affordance - a subtle fill plus a solid border
 * - lives on the block, and its padding is identical to the Runtime block's, so
 * the text never shifts between reading and editing. Asset slots use a dashed
 * accent border instead, so the two input kinds differ in shape as well as
 * colour and survive greyscale.
 *
 * Every text field on every template is one of these, so rich text formatting
 * and variable tokens arrive on all of them at once rather than per template.
 */
export default function PowerSlideTextBlock(props: PowerSlideTextBlockProps) {
  const {
    localizationKey,
    placeholder,
    placeholderFontSize,
    ariaLabel,
    typeStyle,
    color,
    surface,
    paddingInline,
    paddingBlock,
    radius = powerSlideRadius.lg,
    align = "left",
    hug = false,
    readOnly = false,
    variables,
    languageShortcode,
    getValue,
    changeValue,
  } = props;

  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const value = getValue(localizationKey);
  /**
   * The affordance is revealed rather than permanent: a slide covered in
   * filled, outlined boxes reads as a form, and the author cannot see what
   * they are actually making. Hover and focus are enough to find a field, and
   * the placeholder stays visible so an empty region is still discoverable.
   */
  const showAffordance = hovered || focused;

  const font = powerSlideType[typeStyle];
  const textStyle: CSSProperties = {
    ...font,
    color,
    textAlign: align,
    // The editor's own rule is pre-wrap; the hugging eyebrow wants a single
    // line that sizes to its text instead.
    whiteSpace: hug ? "pre" : "pre-wrap",
    width: hug ? undefined : "100%",
  };

  if (readOnly) {
    // Formatting tags are not content: a region holding only markup is empty
    // and disappears, exactly as an untouched one does.
    if (!stripFormattingTags(value).trim()) return null;
    return (
      <RichTextView
        value={value}
        variables={variables}
        style={{
          boxSizing: "border-box",
          paddingInline,
          paddingBlock,
          ...textStyle,
        }}
      />
    );
  }

  return (
    <div
      data-powerslide-block={ariaLabel}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      // Focus events bubble in React, so the block sees the editor's own focus
      // without the editor having to hand it back out.
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        width: hug ? undefined : "100%",
        boxSizing: "border-box",
        paddingInline,
        paddingBlock,
        borderRadius: radius,
        // An inset outline rather than a border: it reproduces Figma's INSIDE
        // stroke and takes up no layout space, so revealing it on hover never
        // shifts the text by a pixel.
        outline: showAffordance
          ? `1px solid ${focused ? surface.fieldFocusBorder : surface.fieldBorder}`
          : "none",
        outlineOffset: -1,
        backgroundColor: showAffordance ? surface.fieldFill : "transparent",
        transition: "background-color 90ms ease, outline-color 90ms ease",
        // Read by the :empty::before rule. A custom property rather than a
        // class so the size travels with the field's own type ramp instead of
        // needing a new CSS rule per display size.
        ...(placeholderFontSize
          ? ({
              "--powerslide-placeholder-size": `${placeholderFontSize}px`,
            } as CSSProperties)
          : {}),
      }}
    >
      <RichTextEditor
        ariaLabel={ariaLabel}
        placeholder={placeholder}
        value={value}
        onCommit={(nextValue) =>
          changeValue(localizationKey, nextValue, languageShortcode)
        }
        variables={variables}
        debounceMs={250}
        // Switching slide, template or language unmounts or repoints this
        // field; the pending edit has to land on the key it was typed into.
        flushOnCleanupKeys={[localizationKey, languageShortcode]}
        contentClassName="powerslide-input"
        contentStyle={textStyle}
        warningClassName="powerslide-warning"
        // The stage renders at 1280px and is scaled down, so the editor's own
        // width would size the suggestion menu against the wrong ruler.
        suggestionsMaxWidth={360}
      />
    </div>
  );
}
